import React, { useState, useEffect } from 'react';
import { DetailsList, DetailsListLayoutMode, SelectionMode, ConstrainMode, IColumn, TextField, Dropdown, MessageBar, MessageBarType, TooltipHost, Icon, Spinner, SpinnerSize } from '@fluentui/react';
import { renderDate } from '../../helpers/HelperMethods';
import DeleteAppPermission from './SiteEditElements/DeleteAppPermission';
import AddAppPermission from './SiteEditElements/AddAppPermission';
import { AppPermissionType } from '../../GlobalConst/globalEnums'
import EditAppPermission from './SiteEditElements/EditAppPermission';

const AppPermissions = ({site, refresh}) => {    
    const [newAppPermission, setNewAppPermission] = useState({})
    const [items, setItems] = useState([])
    const [messageText, setMessageText] = useState('')
    const [isError, setIsError] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [errors, setErrors] = useState({})

    useEffect(() => {
        setItems([...site.appPermissions.map(x => ({...x, oldPermission: x.permission})), newAppPermission])
        setIsLoading(false)
    }, [site.appPermissions])

    const columns: IColumn[] = [
        {
            key: 'appDisplayName',
            name: 'App Display Name',
            fieldName: 'appDisplayName',
            minWidth: 400, 
            maxWidth: 700,
            isResizable: true,
            onRender: (item) => {
                return (
                    item.id
                    ? item.appDisplayName
                    : <div style={newItemDisplayNameStyle}>{newAppPermission.appDisplayName}</div>
                )
            }
        },
        {
            key: 'appId',
            name: 'App ID',
            fieldName: 'appId',
            minWidth: 400, 
            maxWidth: 700,
            isResizable: true,
            onRender: (item) => {
                return (
                    item.id 
                    ? item.appId
                    : <TextField 
                        value={newAppPermission.appId} 
                        onChange={(e, value) => setNewAppPermission({...newAppPermission, appId: value.trim()})} 
                        errorMessage={errors.appId}
                        styles={newAppFieldStyles}
                      />
                )
            }
        },
        {
            key: 'permission',
            name: 'Permission',
            fieldName: 'permission',
            minWidth: 150, 
            maxWidth: 200,
            isResizable: true,
            onRender: (item) => {
                if (item.id) {
                    return  item.isEditing
                            ? <Dropdown 
                                tabIndex={0}
                                options={Object.keys(AppPermissionType).map(x => { return {key: x, text: AppPermissionType[x]} })} 
                                selectedKey={Object.keys(AppPermissionType).find(x => AppPermissionType[x] === item.permission)} 
                                onChange={(e, value) => setItemPermission(item.id, value.text)} 
                              />
                            : item.permission
                } else {
                    return <Dropdown 
                                tabIndex={0}
                                options={Object.keys(AppPermissionType).map(x => { return {key: x, text: AppPermissionType[x]} })} 
                                selectedKey={newAppPermission.permission} 
                                onChange={(e, value) => setNewAppPermission({...newAppPermission, permission: value.key})} 
                                errorMessage={!newAppPermission.permission && errors.permission}
                                styles={newAppFieldStyles}
                            />
                }
            }
        },
        {
            key: 'created',
            name: 'Created',
            fieldName: 'created',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            data: 'number',
            onRender: (item) => {
                return <span>{renderDate(item.created)}</span>
            },
            isPadded: true
        },
        {
            key: 'closed',
            name: 'Closed',
            fieldName: 'closed',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            data: 'number',
            onRender: (item) => {
                return <span>{renderDate(item.closed)}</span>
            },
            isPadded: true
        },
        {
            key: 'actions',
            name: 'Actions',
            minWidth: 50, 
            maxWidth: 200, 
            isResizable: true,
            className: 'appPermissionCellActions',
            onRender: (item) => {
                if (item.id) {
                    if (item.isUpdating) {
                        return (
                            <TooltipHost content='Updates are in progress' styles={actionStyles}>
                                <Icon iconName='workflow' style={iconStyles} />
                            </TooltipHost>
                        )
                    }
                    else if (!item.closed) {
                        return (
                            <div style={actionsStyles}>
                                <EditAppPermission 
                                    appPermission={item} 
                                    siteId={site.id} 
                                    refresh={refreshSite} 
                                    setMessage={setMessage} 
                                    setIsEditing={setIsEditing} 
                                />
                                <DeleteAppPermission 
                                    appPermission={item} 
                                    siteId={site.id} 
                                    refresh={refreshSite} 
                                    setMessage={setMessage} 
                                    setAppIsLoading={(val) => setItemIsLoading(item.id, val)}
                                />
                            </div>
                        )
                    }
                }
                else {
                    return <AddAppPermission 
                                appPermission={newAppPermission} 
                                siteId={site.id} 
                                refresh={(siteId) => refreshSite(siteId, true)} 
                                setMessage={setMessage}
                                setAppPermission={setNewAppPermission}
                                setErrors={setErrors}
                            />
                }
            }
        },
    ]

    const setMessage = (text, isError) => {
        setMessageText(text)
        setIsError(isError)
    }

    const setIsEditing = (itemId) => {
        setItems(items.map(x => x.id === itemId ? {...x, isEditing: true} : x))
    }

    const setItemPermission = (itemId, permission) => {
        setItems(items.map(x => x.id === itemId ? {...x, permission} : x))
    }

    const setItemIsLoading = (itemId, val) => {
        setItems(items.map(x => x.id === itemId ? {...x, isLoading: val} : x))
    }

    const refreshSite = (siteId, isAdding) => {
        if (isAdding)
            setNewAppPermission({})

        setIsLoading(true)
        setItems([])
        refresh(siteId)

        setMessageText('Request has been received. Permissions will be updated in a few minutes.')
    }

    return(
        <div>
            {
                <div style={containerStyles}>
                    <DetailsList 
                        items={items}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        isHeaderVisible={true}
                        selectionMode={SelectionMode.none}
                        styles={listStyles}
                        constrainMode={ConstrainMode.horizontalConstrained}
                    />
                    { isLoading && <Spinner size={SpinnerSize.medium} /> }
                    <div style={messageBarStyles}>
                    {   
                        messageText &&
                        <MessageBar 
                            dismissButtonAriaLabel="Close"
                            messageBarType={isError ? MessageBarType.error : MessageBarType.success}>
                            {messageText}
                        </MessageBar>
                    }
                    </div>
                </div>
            }
        </div>
    );
};

const listStyles = {
    root: {
        width: '100%'
    }
}

const messageBarStyles = {
    marginTop: 20
}

const containerStyles = {
    padding: 20
}
    
const actionStyles = {
    root: {
        margin: 'auto'
    }
}

const iconStyles = {
    cursor: 'default',
    margin: '0 5px',
    fontSize: '16px',
    fontWeight: '600'
}

const actionsStyles = {
    display: 'flex'
}

const newAppFieldStyles = {
    errorMessage: {
        whiteSpace: 'normal'
    }
}

const newItemDisplayNameStyle = {
    lineHeight: '32px'
}

export default AppPermissions;


// WEBPACK FOOTER //
// ./src/components/EditSite/AppPermissions.js